// src/components/Diagnosis.js
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  CardContent,
  Typography,
  TextField,
  MenuItem,
} from "@mui/material";
import { AuthContext } from "../../../context/AuthContext";
import {
  clinicalStageInitialValues,
  mapClinicalStageResponse,
} from "../../../schema/clinicalStageInitialValues";
import { authHttp } from "../../../services/commonHttpService";
import { apisPath } from "../../../utils/path";
import { Formik } from "formik";
import { clinicalStageSchema } from "../../../schema/clinicalStageSchema";
import { CommonContext } from "../../../context/CommonContext";

const ClinicalStage = ({ submitForm, formResult, currentStep, patientId }) => {
  const { setIsLoading, setAlertState } = useContext(CommonContext);
  const { userId } = useContext(AuthContext);

  const formRef = useRef(null);

  const [initialValues, setInitialValues] = useState(clinicalStageInitialValues);
  const [isFocus, setIsFocus] = useState(false);

  // helper: prepare payload for POST (kept inside file)
  // ------------------------------
  const refactorDataBeforeSubmit = (values) => {
    const payload = {
      patient_id: Number(patientId),
      user_id: Number(userId),
      disease_extent: values?.disease_extent,
      clinical_t: values?.clinical_t,
      clinical_n: values?.clinical_n,
      clinical_m: values?.clinical_m,
      pathological_t: values?.pathological_t,
      pathological_n: values?.pathological_n,
      pathological_m: values?.pathological_m,
      version: 1,
      completed_step: 2,
    };

    if (values?.disease_extent === "Others") {
      payload.disease_extent_other = values?.disease_extent_other;
    }

    return payload;
  };

  // Fetch existing clinical stage data (GET) — handle 404 like DiagnosticDetails
  const fetchStepData = async () => {
    if (!patientId) return;
    setIsLoading(true);
    try {
      const response = await authHttp.get(
        `${apisPath.getPatientTreatmentStep}/${patientId}/2`
      );

      if (response?.data?.data) {
        const mapped = mapClinicalStageResponse(response?.data?.data);
        setInitialValues(mapped);
      }
    } catch (error) {
      // If 404 -> no previous data (expected). Do nothing.
      if (error?.response?.status !== 404) {
        setAlertState({
          open: true,
          title: error?.response?.data?.message,
          btnTxt: "CLOSE",
          status: "fail",
          isSessionExpire: error?.response?.status === 401,
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
  if (currentStep === 1 && patientId) {
    fetchStepData();
  }
}, [currentStep, patientId]);


  useEffect(() => {
    if (submitForm) {
      // Trigger Formik submit
      formRef?.current?.handleSubmit();

      // small delay to allow validation to populate errors
      setTimeout(() => {
        if (Object.keys(formRef?.current?.errors || {}).length !== 0) {
          setIsFocus(true);
        }
      }, 100);

      // reset trigger in parent (parent expects child to call formResult(false) or we do it here)
      formResult(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitForm]);

  // Focus first error & show alert (same as DiagnosticDetails)
  // ------------------------------
  useEffect(() => {
    const errObj = formRef?.current?.errors || {};
    if (isFocus && Object.keys(errObj).length) {
      const keys = Object.keys(errObj);
      const firstField = window.document.getElementsByName(keys[0])[0];
      if (firstField) firstField.focus();

      setAlertState({
        open: true,
        title: Object.values(errObj)[0],
        btnTxt: "OK",
        status: "fail",
      });

      setIsFocus(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFocus, formRef?.current?.errors]);

  // Submit Handler (POST API)
  // --------------------------------
  const submitHandler = async (values) => {
    setIsLoading(true);
    try {
      const payload = refactorDataBeforeSubmit(values);
      const response = await authHttp.post(`${apisPath.patientTreatmentStep}/2`, payload);

      if (response?.data?.success || response?.status === 200) {
        setAlertState({
          open: true,
          title: "Clinical stage saved successfully",
          btnTxt: "CLOSE",
          status: "success",
        });

        formResult(true); // notify parent to proceed
      } else {
        // handle unexpected but non-error responses
        setAlertState({
          open: true,
          title: response?.data?.message,
          btnTxt: "CLOSE",
          status: "success",
        });
        formResult(true);
      }
    } catch (error) {
      setAlertState({
        open: true,
        title: error?.response?.data?.message,
        btnTxt: "CLOSE",
        status: "fail",
        isSessionExpire: error?.response?.status === 401,
      });
      formResult(false);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <CardContent>
      <Formik
        initialValues={initialValues}
        enableReinitialize
        validationSchema={clinicalStageSchema}
        onSubmit={submitHandler}
        innerRef={formRef}
      >
        {({ values, errors, touched, handleChange, setFieldValue }) => (
          <>
            {/* --------------------------
                Clinical Extent of Disease
            --------------------------- */}
            <h6 className="header-sub mb-3">Clinical Stage</h6>

            <div className="row mb-4 align-items-start">
              <div className="col-md-4 col-12 mt-4">  
                
                <TextField
                  fullWidth
                  select
                  variant="standard"
                  name="disease_extent"
                  label="Clinical Extent of Disease"
                  value={values?.disease_extent}
                  onChange={(e) => setFieldValue("disease_extent", e.target.value)}
                  // error={touched.disease_extent && !!errors.disease_extent}
                  // helperText={touched.disease_extent && errors.disease_extent}
                >
                  <MenuItem value="In-situ">In-situ</MenuItem>
                  <MenuItem value="Benign">Benign</MenuItem>
                  <MenuItem value="Borderline">Borderline</MenuItem>
                  <MenuItem value="Pre-invasive">Pre-invasive</MenuItem>
                  <MenuItem value="Localized">Localized</MenuItem>
                  <MenuItem value="Direct extension">Direct extension</MenuItem>
                  <MenuItem value="Regional nodes">Regional nodes</MenuItem>
                  <MenuItem value="Direct extension with regional nodes">
                    Direct extension with regional nodes
                  </MenuItem>
                  <MenuItem value="Distant metastasis">Distant metastasis</MenuItem>
                  <MenuItem value="Not applicable">Not applicable</MenuItem>
                  <MenuItem value="Recurrence">Recurrence</MenuItem>
                  <MenuItem value="Unknown primary">Unknown primary</MenuItem>
                  <MenuItem value="Others">Others (specify)</MenuItem>
                  <MenuItem value="Unknown">Unknown</MenuItem>
                </TextField>
              </div>

              {values?.disease_extent === "Others" && (
                <div className="col-md-4 col-12">
                  <TextField
                    fullWidth
                    variant="standard"
                    multiline
                    rows={2}
                    name="disease_extent_other"
                    label="Specify"
                    value={values?.disease_extent_other}
                    onChange={handleChange}
                    // error={
                    //   touched.disease_extent_other &&
                    //   !!errors.disease_extent_other
                    // }
                    // helperText={
                    //   touched.disease_extent_other &&
                    //   errors.disease_extent_other
                    // }
                  />
                </div>
              )}
            </div>

            {/* --------------------------
                Clinical Stage Group
            --------------------------- */}
            <h6 className="header-sub mb-3">Staging System</h6>

            <Typography sx={{ fontWeight: 400 }}>
              Clinical Stage Group
            </Typography>

            <div className="row">
              <div className="col-md-3 col-12 mt-3">
                <TextField
                  fullWidth
                  variant="standard"
                  name="clinical_t"
                  label="T (Tumor)"
                  value={values?.clinical_t}
                  onChange={handleChange}
                  // error={touched.clinical_t && !!errors.clinical_t}
                  // helperText={touched.clinical_t && errors.clinical_t}
                />
              </div>

              <div className="col-md-3 col-12 mt-3">
                <TextField
                  fullWidth
                  variant="standard"
                  name="clinical_n"
                  label="N (Nodes)"
                  value={values?.clinical_n}
                  onChange={handleChange}
                  // error={touched.clinical_n && !!errors.clinical_n}
                  // helperText={touched.clinical_n && errors.clinical_n}
                />
              </div>

              <div className="col-md-3 col-12 mt-3">
                <TextField
                  fullWidth
                  variant="standard"
                  name="clinical_m"
                  label="M (Metastasis)"
                  value={values?.clinical_m}
                  onChange={handleChange}
                  // error={touched.clinical_m && !!errors.clinical_m}
                  // helperText={touched.clinical_m && errors.clinical_m}
                />
              </div>
            </div>

            {/* --------------------------
                Pathological Stage Group
            --------------------------- */}
            <div className="row mt-5">
              <Typography sx={{ fontWeight: 400 }}>
                Pathological Stage Group
              </Typography>

              <div className="col-md-3 col-12 mt-3">
                <TextField
                  fullWidth
                  variant="standard"
                  name="pathological_t"
                  label="T (Tumor)"
                  value={values?.pathological_t}
                  onChange={handleChange}
                  // error={touched.pathological_t && !!errors.pathological_t}
                  // helperText={touched.pathological_t && errors.pathological_t}
                />
              </div>

              <div className="col-md-3 col-12 mt-3">
                <TextField
                  fullWidth
                  variant="standard"
                  name="pathological_n"
                  label="N (Nodes)"
                  value={values?.pathological_n}
                  onChange={handleChange}
                  // error={touched.pathological_n && !!errors.pathological_n}
                  // helperText={touched.pathological_n && errors.pathological_n}
                />
              </div>

              <div className="col-md-3 col-12 mt-3">
                <TextField
                  fullWidth
                  variant="standard"
                  name="pathological_m"
                  label="M (Metastasis)"
                  value={values?.pathological_m}
                  onChange={handleChange}
                  // error={touched.pathological_m && !!errors.pathological_m}
                  // helperText={touched.pathological_m && errors.pathological_m}
                />
              </div>
            </div>
          </>
        )}
      </Formik>
    </CardContent>
  );
};

export default ClinicalStage;
